import { Box, Stack, Typography, Tooltip, Chip, Paper, LinearProgress } from '@mui/material';
import {
  InfoOutlined,
  WarningAmberRounded,
  BlockRounded,
  MarkEmailReadRounded,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  acumuladoCampanas,
  metricasCampana,
  porcentaje,
  razonSinDatos,
  type CampanaMedible,
} from '../../../lib/campaign-metrics';

/** La explicación que corresponde a POR QUÉ no hay datos.
 *
 *  Las tres razones piden cosas distintas de quien mira: «anterior a la
 *  medición» se acepta, «todavía no llegan» se vuelve a mirar en un rato, y
 *  «nada salió» hay que atenderlo. Una sola frase para las tres mandaría a
 *  archivar la única que es un problema. */
export function useRazonSinDatos(): (row: CampanaMedible & { sentAt?: Date }) => string {
  const { t } = useTranslation('sis');
  return (row) =>
    ({
      'nada-salio': t('campaign.results.nothingSent'),
      'anterior-a-la-medicion': t('campaign.results.predates'),
      'todavia-no-llegan': t('campaign.results.pending'),
    })[razonSinDatos(row)];
}

// ─── Lo que pasó con los correos, en pantalla ────────────────────────────────
//
// TODO LO QUE DECIDE QUÉ NÚMERO SE MUESTRA vive en `lib/campaign-metrics.ts`.
// Acá sólo se dibuja. La separación importa porque la misma cuenta la usan la
// franja de arriba, la fila del historial y este detalle: si cada una la hiciera
// por su cuenta, la franja diría un porcentaje y la fila otro.
//
// LA REGLA QUE NO SE PUEDE ROMPER: `undefined` se dibuja «Sin datos», nunca
// `0`. Un cero en una campaña que nunca se midió afirma que nadie la recibió.

/** Un número con su porcentaje, o «Sin datos» cuando no lo hay — con la razón
 *  a mano, porque no es lo mismo que la campaña sea vieja, que acabe de salir o
 *  que no haya salido. */
export function Cifra({
  valor,
  sobre,
  sinDatos,
}: {
  valor: number | undefined;
  sobre?: number;
  sinDatos: string;
}) {
  const { t } = useTranslation('sis');
  if (valor === undefined) {
    return (
      <Tooltip title={sinDatos} arrow>
        <Typography
          component="span"
          variant="caption"
          color="text.secondary"
          sx={{ cursor: 'help', borderBottom: '1px dotted', borderColor: 'text.disabled' }}
        >
          {t('campaign.results.noData')}
        </Typography>
      </Tooltip>
    );
  }
  const pct = sobre === undefined ? undefined : porcentaje(valor, sobre);
  return (
    <Stack direction="row" spacing={0.75} alignItems="baseline" justifyContent="flex-end">
      <Typography component="span" variant="body2" fontWeight={600}>
        {valor}
      </Typography>
      {pct !== undefined && (
        <Typography component="span" variant="caption" color="text.secondary">
          {pct}%
        </Typography>
      )}
    </Stack>
  );
}

/** El aviso de rebotes al lado del asunto. Un rebote es ACCIONABLE —esa
 *  dirección hay que corregirla— así que se ve sin abrir nada. */
export function ChipRebotes({ rebotes }: { rebotes: number | undefined }) {
  const { t } = useTranslation('sis');
  // LOS DOS CASOS NO SE DIBUJAN, y por razones distintas: sin medir no hay nada
  // que avisar, y cero rebotes es una buena noticia que no merece un aviso. Se
  // escribe explícito porque en este módulo confundir `undefined` con `0` es
  // el error que se está evitando en todos lados.
  if (rebotes === undefined || rebotes === 0) return null;
  return (
    <Tooltip title={t('campaign.results.bouncedHelp')} arrow>
      <Chip
        size="small"
        color="warning"
        variant="outlined"
        icon={<WarningAmberRounded />}
        label={t('campaign.results.bouncedChip', { count: rebotes })}
      />
    </Tooltip>
  );
}

/** Una nota al pie. Con `alerta` cuando no es contexto sino algo que atender:
 *  una campaña que no salió pide una acción, no una aclaración. */
function Nota({ texto, alerta }: { texto: string; alerta?: boolean }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="flex-start">
      {alerta ? (
        <WarningAmberRounded
          sx={{ fontSize: 14, color: 'warning.main', mt: '2px', flexShrink: 0 }}
        />
      ) : (
        <InfoOutlined sx={{ fontSize: 14, color: 'text.disabled', mt: '2px', flexShrink: 0 }} />
      )}
      <Typography variant="caption" color={alerta ? 'warning.dark' : 'text.secondary'}>
        {texto}
      </Typography>
    </Stack>
  );
}

/**
 * La franja de arriba: cómo va el correo en general.
 *
 * NO APARECE SI NADA ESTÁ MEDIDO. Un resumen con cuatro guiones no informa y
 * ocupa el lugar de lo que sí importa; mientras todo el historial sea anterior
 * al webhook, la pantalla se ve como antes.
 */
export function ResumenEnvios({ rows }: { rows: (CampanaMedible & { sentAt?: Date })[] }) {
  // `sentAt` VA EN EL TIPO aunque la suma no lo use para sumar: sin él,
  // `razonSinDatos` no puede distinguir una campaña vieja de una recién
  // enviada, y las atribuiría todas a «están por llegar» sin que el compilador
  // dijera nada. La dependencia se declara para que no se pierda en silencio.
  const { t } = useTranslation('sis');
  const total = acumuladoCampanas(rows);
  if (!total.medida) return null;

  const tasaEntrega = porcentaje(total.entregados, total.enviados);

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, p: 2, mb: 2 }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        useFlexGap
        mb={1.5}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <MarkEmailReadRounded fontSize="small" color="primary" />
          <Typography variant="subtitle2" fontWeight={700}>
            {t('campaign.results.bandTitle')}
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {t('campaign.results.basedOn', { count: total.campanasMedidas })}
        </Typography>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
          gap: 2,
        }}
      >
        <Metrica etiqueta={t('campaign.results.sent')} valor={total.enviados} />
        <Metrica
          etiqueta={t('campaign.results.delivered')}
          valor={total.entregados}
          pct={tasaEntrega}
        />
        <Metrica
          etiqueta={t('campaign.results.opened')}
          valor={total.aperturas}
          pct={porcentaje(total.aperturas, total.enviados)}
        />
        <Metrica
          etiqueta={t('campaign.results.bounced')}
          valor={total.rebotes}
          destacado={(total.rebotes ?? 0) > 0}
        />
      </Box>

      {tasaEntrega !== undefined && (
        <LinearProgress
          variant="determinate"
          value={Math.min(tasaEntrega, 100)}
          color={tasaEntrega >= 95 ? 'success' : tasaEntrega >= 80 ? 'warning' : 'error'}
          sx={{ mt: 2, height: 6, borderRadius: 3 }}
        />
      )}

      <Stack spacing={0.5} mt={1.5}>
        {/* UN MOTIVO POR LÍNEA, y no una frase que los resuma.
            Visto en producción el 28-09-2026: la franja decía «16 campañas son
            anteriores a la medición» y una de las 16 era de la noche anterior y
            no había salido. Afirmar un motivo que no se sabe archiva justo la
            campaña que hay que mirar. */}
        {total.sinDatosPorRazon['nada-salio'] > 0 && (
          <Nota
            alerta
            texto={t('campaign.results.excludedNothingSent', {
              count: total.sinDatosPorRazon['nada-salio'],
            })}
          />
        )}
        {total.sinDatosPorRazon['todavia-no-llegan'] > 0 && (
          <Nota
            texto={t('campaign.results.excludedPending', {
              count: total.sinDatosPorRazon['todavia-no-llegan'],
            })}
          />
        )}
        {total.sinDatosPorRazon['anterior-a-la-medicion'] > 0 && (
          <Nota
            texto={t('campaign.results.excludedOld', {
              count: total.sinDatosPorRazon['anterior-a-la-medicion'],
            })}
          />
        )}
        <Nota texto={t('campaign.results.eventsNotPeople')} />
        <Nota texto={t('campaign.results.opensCaveat')} />
      </Stack>
    </Paper>
  );
}

function Metrica({
  etiqueta,
  valor,
  pct,
  destacado,
}: {
  etiqueta: string;
  valor: number | undefined;
  pct?: number | undefined;
  destacado?: boolean;
}) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.3 }}>
        {etiqueta}
      </Typography>
      <Stack direction="row" spacing={0.75} alignItems="baseline">
        <Typography
          variant="h6"
          fontWeight={700}
          color={destacado ? 'warning.dark' : 'text.primary'}
          sx={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {/* NO `valor ?? 0`. Hoy no se llega acá sin dato —la franja mira
              `medida` antes de dibujar— pero un `?? 0` en camino de render es
              justo lo que reintroduce la mentira en cuanto alguien mueva esa
              guarda. Que falte el número se ve; un cero inventado, no. */}
          {valor === undefined ? '—' : valor}
        </Typography>
        {pct !== undefined && (
          <Typography variant="body2" color="text.secondary">
            {pct}%
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

/**
 * El detalle de UNA campaña, dentro de la fila desplegada.
 *
 * Responde las tres preguntas en orden: salió, llegó, la leyeron — y además
 * qué hay que arreglar (rebotes y quejas), que es lo único accionable.
 */
export function DetalleCampana({ row }: { row: CampanaMedible & { sentAt?: Date } }) {
  const { t } = useTranslation('sis');
  const razon = useRazonSinDatos();
  const m = metricasCampana(row);

  if (!m.medida) return <Nota texto={razon(row)} />;

  const filas: { etiqueta: string; valor: number | undefined; ayuda?: string; alerta?: boolean }[] =
    [
      { etiqueta: t('campaign.results.sent'), valor: m.enviados },
      { etiqueta: t('campaign.results.delivered'), valor: m.entregados },
      { etiqueta: t('campaign.results.opened'), valor: m.aperturas },
      { etiqueta: t('campaign.results.clicked'), valor: m.clics },
      {
        etiqueta: t('campaign.results.bounced'),
        valor: m.rebotes,
        ayuda: t('campaign.results.bouncedHelp'),
        alerta: (m.rebotes ?? 0) > 0,
      },
      {
        etiqueta: t('campaign.results.complained'),
        valor: m.quejas,
        ayuda: t('campaign.results.complainedHelp'),
        alerta: (m.quejas ?? 0) > 0,
      },
    ];

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2" fontWeight={700}>
        {t('campaign.results.detailTitle')}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
          gap: 1.5,
        }}
      >
        {filas.map((f) => (
          <Stack key={f.etiqueta} direction="row" spacing={1} alignItems="center">
            {f.alerta && <BlockRounded sx={{ fontSize: 16, color: 'warning.main' }} />}
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                {f.ayuda ? (
                  <Tooltip title={f.ayuda} arrow>
                    <Box component="span" sx={{ cursor: 'help' }}>
                      {f.etiqueta}
                    </Box>
                  </Tooltip>
                ) : (
                  f.etiqueta
                )}
              </Typography>
              <Stack direction="row" spacing={0.75} alignItems="baseline">
                <Typography
                  variant="body1"
                  fontWeight={700}
                  sx={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {f.valor === undefined ? '—' : f.valor}
                </Typography>
                {f.etiqueta !== t('campaign.results.sent') &&
                  porcentaje(f.valor, m.enviados) !== undefined && (
                    <Typography variant="caption" color="text.secondary">
                      {porcentaje(f.valor, m.enviados)}%
                    </Typography>
                  )}
              </Stack>
            </Box>
          </Stack>
        ))}
      </Box>
      <Nota texto={t('campaign.results.eventsNotPeople')} />
    </Stack>
  );
}
